/** @format */

import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import type {
  VirtualGridProps,
  Column,
  CellSelection,
  RangeSelection,
} from "./types";
import VirtualRow from "./VirtualRow";
import "./VirtualGrid.css";
import ColumnFilterMenu from "./ColumnFilterMenu";
import { createPortal } from "react-dom";

const VirtualGrid: React.FC<VirtualGridProps> = ({
  dataSource,
  originalDataSource,
  columns: propsColumns,
  rowHeight = 30,
  headerHeight = 30,
  overscan = 3, // Reduced from 5 to 3 for better performance
  style,
  className = "",
  onRowClick,
  rowClassName,
  sortState,
  onSort,
  onCellSelection,
  enableCellSelection = false,
  enableRowSelection = false,
  rowKey,
  selectedRowKeys: controlledSelectedRowKeys,
  onRowSelectionChange,
  enableColumnResize = false,
  onColumnResize,
  columnWidths = {},
  enableColumnReorder = false,
  onColumnReorder,
  theme = {},
  filters,
  onFiltersChange,
  onCellEdit,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(600);
  const scrollRAF = useRef<number | null>(null);

  // Cell selection state
  const [selectedCells, setSelectedCells] = useState<CellSelection[]>([]);
  const [, setSelectedRange] = useState<RangeSelection | null>(null);
  const [activeCell, setActiveCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);

  // Performance optimization: Convert selectedCells to Set for O(1) lookup
  const selectedCellsSet = useMemo(() => {
    const set = new Set<string>();
    selectedCells.forEach((cell) => {
      set.add(`${cell.rowIndex}-${cell.columnKey}`);
    });
    return set;
  }, [selectedCells]);

  // Column resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  // Column reorder state
  const [draggedColumnIndex, setDraggedColumnIndex] = useState<number | null>(
    null
  );
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(
    null
  );

  // Store columns in local state for reordering (MUST be before generateRangeCells)
  const [columns, setColumns] = useState(propsColumns);

  // Filter menu UI state
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [filterPosition, setFilterPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [openFilterValues, setOpenFilterValues] = useState<Array<
    string | number | boolean
  > | null>(null);

  // Row selection state (supports controlled and uncontrolled)
  const [uncontrolledSelectedRowKeys, setUncontrolledSelectedRowKeys] =
    useState<Array<string | number>>([]);
  const selectedRowKeys =
    controlledSelectedRowKeys ?? uncontrolledSelectedRowKeys;

  // In controlled mode, parent updates may arrive on the next tick. When the
  // user clicks multiple checkboxes quickly, relying on props can produce
  // stale reads ([1] then [2] instead of [1,2]). Track the latest keys in a
  // ref to compute next state synchronously across rapid interactions.
  const latestSelectedRowKeysRef =
    useRef<Array<string | number>>(selectedRowKeys);
  useEffect(() => {
    latestSelectedRowKeysRef.current = selectedRowKeys;
  }, [selectedRowKeys]);

  // Selection state for async "select all" operation
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const getRowKey = useCallback(
    (record: Record<string, unknown>, index: number): string | number => {
      if (
        rowKey &&
        record &&
        Object.prototype.hasOwnProperty.call(record, rowKey)
      ) {
        return (record as Record<string, unknown>)[rowKey] as string | number;
      }
      return index;
    },
    [rowKey]
  );

  // Performance optimization: Convert selectedRowKeys to Set for O(1) lookup
  const selectedRowKeysSet = useMemo(
    () => new Set(selectedRowKeys),
    [selectedRowKeys]
  );

  // Memoized header checkbox state (avoids recalculating on every render)
  const headerCheckboxState = useMemo(() => {
    if (!enableRowSelection || dataSource.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const selectedCount = selectedRowKeys.length;
    const allSelected =
      selectedCount > 0 && selectedCount === dataSource.length;
    const someSelected = selectedCount > 0 && selectedCount < dataSource.length;

    return {
      checked: allSelected,
      indeterminate: someSelected,
    };
  }, [enableRowSelection, selectedRowKeys.length, dataSource.length]);

  const emitRowSelection = useCallback(
    (nextKeys: Array<string | number>, skipRowsLookup = false) => {
      if (onRowSelectionChange) {
        // For large selections, skip building selectedRows array (can be millions of operations)
        if (skipRowsLookup || nextKeys.length > 10000) {
          // Just pass keys, let consumer build rows if needed
          onRowSelectionChange(nextKeys, []);
          return;
        }

        // For small selections, build the selectedRows array
        const keyToRow = new Map<string | number, Record<string, unknown>>();
        dataSource.forEach((row, idx) => {
          const key = getRowKey(row as never, idx);
          keyToRow.set(key, row as Record<string, unknown>);
        });

        const selectedRows = nextKeys
          .map((k) => keyToRow.get(k))
          .filter(Boolean) as Record<string, unknown>[];

        onRowSelectionChange(nextKeys, selectedRows);
      }
    },
    [onRowSelectionChange, dataSource, getRowKey]
  );

  // Ref to track if we should emit selection change (avoid emitting on mount)
  const shouldEmitSelectionRef = useRef(false);
  const lastEmittedKeysRef = useRef<string>("");

  const toggleRow = useCallback(
    (record: Record<string, unknown>, index: number) => {
      if (!enableRowSelection) return;

      const key = getRowKey(record, index);

      // Use functional state update to avoid stale closure issue
      const updateKeys = (prevKeys: Array<string | number>) => {
        // Use Set for O(1) lookup instead of O(n) includes
        const prevSet = new Set(prevKeys);
        const exists = prevSet.has(key);
        return exists ? prevKeys.filter((k) => k !== key) : [...prevKeys, key];
      };

      if (controlledSelectedRowKeys === undefined) {
        // Mark that we should emit after state updates
        shouldEmitSelectionRef.current = true;
        setUncontrolledSelectedRowKeys((prevKeys) => updateKeys(prevKeys));
      } else {
        // For controlled mode, compute and emit immediately
        const nextKeys = updateKeys(latestSelectedRowKeysRef.current);
        // Optimistically update the ref so consecutive clicks within the same
        // render frame build upon the most recent intent, even before parent
        // re-renders with new props.
        latestSelectedRowKeysRef.current = nextKeys;
        emitRowSelection(nextKeys);
      }
    },
    [enableRowSelection, getRowKey, controlledSelectedRowKeys, emitRowSelection]
  );

  // Emit selection change when uncontrolled selection actually updates
  useEffect(() => {
    if (
      shouldEmitSelectionRef.current &&
      controlledSelectedRowKeys === undefined
    ) {
      shouldEmitSelectionRef.current = false;

      // Only emit if keys actually changed (avoid duplicate emissions)
      const keysString = uncontrolledSelectedRowKeys.join(",");
      if (keysString !== lastEmittedKeysRef.current) {
        lastEmittedKeysRef.current = keysString;
        emitRowSelection(uncontrolledSelectedRowKeys);
      }
    }
  }, [
    uncontrolledSelectedRowKeys,
    controlledSelectedRowKeys,
    emitRowSelection,
  ]);

  // toggleAllVisible will be defined after computing visibleRows

  // Update columns when props change
  useEffect(() => {
    setColumns(propsColumns);
  }, [propsColumns]);

  // Reset scroll to top when sort state changes (not on every dataSource change)
  useEffect(() => {
    if (sortState?.columnKey) {
      // Only reset when actively sorting
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
        setScrollTop(0);
      }
    }
  }, [sortState?.columnKey, sortState?.direction]);

  // Generate all cells in a range
  const generateRangeCells = useCallback(
    (range: RangeSelection): CellSelection[] => {
      const cells: CellSelection[] = [];
      const startRow = Math.min(range.startRow, range.endRow);
      const endRow = Math.max(range.startRow, range.endRow);

      const columnKeys = columns.map((col) => col.key);
      const startColIndex = Math.min(
        columnKeys.indexOf(range.startColumn),
        columnKeys.indexOf(range.endColumn)
      );
      const endColIndex = Math.max(
        columnKeys.indexOf(range.startColumn),
        columnKeys.indexOf(range.endColumn)
      );

      for (let row = startRow; row <= endRow; row++) {
        for (
          let colIndex = startColIndex;
          colIndex <= endColIndex;
          colIndex++
        ) {
          const columnKey = columnKeys[colIndex];
          const value = dataSource[row]?.[columnKey];
          cells.push({
            rowIndex: row,
            columnKey,
            value,
          });
        }
      }

      return cells;
    },
    [columns, dataSource]
  );

  // Handle cell click for selection
  const handleCellClick = useCallback(
    (
      rowIndex: number,
      columnKey: string,
      value: unknown,
      event: React.MouseEvent
    ) => {
      if (!enableCellSelection) return;

      event.preventDefault();
      event.stopPropagation();

      const newSelection: CellSelection = {
        rowIndex,
        columnKey,
        value,
      };

      if (event.shiftKey && activeCell) {
        // Range selection with Shift
        const range: RangeSelection = {
          startRow: Math.min(activeCell.rowIndex, rowIndex),
          endRow: Math.max(activeCell.rowIndex, rowIndex),
          startColumn: activeCell.columnKey,
          endColumn: columnKey,
        };

        const rangeCells = generateRangeCells(range);
        setSelectedRange(range);
        setSelectedCells(rangeCells);
        onCellSelection?.(rangeCells);
      } else if (event.ctrlKey || event.metaKey) {
        // Individual cell selection with Ctrl/Cmd
        const isSelected = selectedCells.some(
          (cell) => cell.rowIndex === rowIndex && cell.columnKey === columnKey
        );

        let newSelectedCells: CellSelection[];
        if (isSelected) {
          newSelectedCells = selectedCells.filter(
            (cell) =>
              !(cell.rowIndex === rowIndex && cell.columnKey === columnKey)
          );
        } else {
          newSelectedCells = [...selectedCells, newSelection];
        }

        setSelectedRange(null);
        setSelectedCells(newSelectedCells);
        onCellSelection?.(newSelectedCells);
      } else {
        // Single cell selection
        setSelectedRange(null);
        setSelectedCells([newSelection]);
        setActiveCell({ rowIndex, columnKey });
        onCellSelection?.([newSelection]);
      }
    },
    [
      enableCellSelection,
      selectedCells,
      activeCell,
      generateRangeCells,
      onCellSelection,
    ]
  );

  // Handle mouse down for drag selection
  const handleCellMouseDown = useCallback(
    (rowIndex: number, columnKey: string, event: React.MouseEvent) => {
      if (!enableCellSelection) return;

      event.preventDefault();
      setIsDragging(true);
      setDragStart({ rowIndex, columnKey });
      setActiveCell({ rowIndex, columnKey });
    },
    [enableCellSelection]
  );

  // Handle mouse move for drag selection
  const handleCellMouseMove = useCallback(
    (rowIndex: number, columnKey: string) => {
      if (!enableCellSelection || !isDragging || !dragStart) return;

      const range: RangeSelection = {
        startRow: Math.min(dragStart.rowIndex, rowIndex),
        endRow: Math.max(dragStart.rowIndex, rowIndex),
        startColumn: dragStart.columnKey,
        endColumn: columnKey,
      };

      const rangeCells = generateRangeCells(range);
      setSelectedRange(range);
      setSelectedCells(rangeCells);
      onCellSelection?.(rangeCells);
    },
    [
      enableCellSelection,
      isDragging,
      dragStart,
      generateRangeCells,
      onCellSelection,
    ]
  );

  // Handle mouse up to end drag
  const handleCellMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Global mouse events for drag selection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setDragStart(null);
    };

    if (isDragging) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
    }
  }, [isDragging]);

  // Column resize handlers
  const handleResizeStart = useCallback(
    (columnKey: string, event: React.MouseEvent) => {
      if (!enableColumnResize || !onColumnResize) return;
      event.preventDefault();
      event.stopPropagation();

      const column = columns.find((col) => col.key === columnKey);
      if (!column) return;

      const currentWidth = columnWidths[columnKey] || column.width || 150;
      setIsResizing(true);
      setResizingColumn(columnKey);
      setResizeStartX(event.clientX);
      setResizeStartWidth(currentWidth);
    },
    [enableColumnResize, columns, columnWidths, onColumnResize]
  );

  const handleResizeMove = useCallback(
    (event: MouseEvent) => {
      if (!isResizing || !resizingColumn) return;

      const diff = event.clientX - resizeStartX;
      const newWidth = Math.max(50, resizeStartWidth + diff);

      if (onColumnResize) {
        onColumnResize(resizingColumn, newWidth);
      }
    },
    [isResizing, resizingColumn, resizeStartX, resizeStartWidth, onColumnResize]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  // Global mouse events for column resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Column reorder handlers
  const handleColumnDragStart = useCallback(
    (index: number) => {
      if (!enableColumnReorder) return;
      setDraggedColumnIndex(index);
    },
    [enableColumnReorder]
  );

  const handleColumnDragOver = useCallback(
    (index: number, event: React.DragEvent) => {
      if (!enableColumnReorder || draggedColumnIndex === null) return;
      event.preventDefault();

      if (draggedColumnIndex !== index) {
        setDragOverColumnIndex(index);
      }
    },
    [enableColumnReorder, draggedColumnIndex]
  );

  const handleColumnDrop = useCallback(
    (targetIndex: number) => {
      if (!enableColumnReorder || draggedColumnIndex === null) return;

      if (draggedColumnIndex !== targetIndex) {
        // Reorder columns locally for immediate visual feedback
        const newColumns = [...columns];
        const [removed] = newColumns.splice(draggedColumnIndex, 1);
        newColumns.splice(targetIndex, 0, removed);
        setColumns(newColumns);

        // Call parent callback
        if (onColumnReorder) {
          onColumnReorder(draggedColumnIndex, targetIndex);
        }
      }

      setDraggedColumnIndex(null);
      setDragOverColumnIndex(null);
    },
    [enableColumnReorder, draggedColumnIndex, columns, onColumnReorder]
  );

  // Handle column sort
  const handleColumnSort = useCallback(
    (column: Column) => {
      if (!column.sortable || !onSort) return;

      let newDirection: "asc" | "desc" | null = "asc";

      if (sortState?.columnKey === column.key) {
        if (sortState.direction === "asc") {
          newDirection = "desc";
        } else if (sortState.direction === "desc") {
          newDirection = null;
        }
      }

      onSort(column.key, newDirection);
    },
    [sortState, onSort]
  );

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumnIndex(null);
    setDragOverColumnIndex(null);
  }, []);

  // Copy selected cells to clipboard (Excel-compatible format)
  const copySelectedCells = useCallback(() => {
    if (selectedCells.length === 0) return;

    // Group cells by row and sort
    const cellsByRow = new Map<
      number,
      Map<string, { colIndex: number; value: unknown }>
    >();
    const columnKeys = columns.map((col) => col.key);

    selectedCells.forEach((cell) => {
      if (!cellsByRow.has(cell.rowIndex)) {
        cellsByRow.set(cell.rowIndex, new Map());
      }
      const colIndex = columnKeys.indexOf(cell.columnKey);
      cellsByRow.get(cell.rowIndex)!.set(cell.columnKey, {
        colIndex,
        value: cell.value,
      });
    });

    // Sort rows and build Excel-compatible text
    const sortedRows = Array.from(cellsByRow.entries()).sort(
      (a, b) => a[0] - b[0]
    );

    const text = sortedRows
      .map(([, rowCells]) => {
        // Sort cells by column index within each row
        const sortedCells = Array.from(rowCells.values()).sort(
          (a, b) => a.colIndex - b.colIndex
        );

        // Join cells in row with tabs
        return sortedCells
          .map((cell) => {
            const value = cell.value;
            // Handle values that might contain tabs or newlines
            const stringValue = String(value ?? "");
            if (
              stringValue.includes("\t") ||
              stringValue.includes("\n") ||
              stringValue.includes('"')
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          })
          .join("\t");
      })
      .join("\n"); // Join rows with newlines

    navigator.clipboard.writeText(text).catch(console.error);
  }, [selectedCells, columns]);

  // Keyboard navigation
  const handleKeyboardNavigation = useCallback(
    (event: KeyboardEvent) => {
      if (!enableCellSelection || !activeCell) return;

      const columnKeys = columns.map((col) => col.key);
      const currentColIndex = columnKeys.indexOf(activeCell.columnKey);

      let newRowIndex = activeCell.rowIndex;
      let newColIndex = currentColIndex;

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          newRowIndex = Math.max(0, activeCell.rowIndex - 1);
          break;
        case "ArrowDown":
          event.preventDefault();
          newRowIndex = Math.min(
            dataSource.length - 1,
            activeCell.rowIndex + 1
          );
          break;
        case "ArrowLeft":
          event.preventDefault();
          newColIndex = Math.max(0, currentColIndex - 1);
          break;
        case "ArrowRight":
          event.preventDefault();
          newColIndex = Math.min(columnKeys.length - 1, currentColIndex + 1);
          break;
        case "Home":
          event.preventDefault();
          newColIndex = 0;
          break;
        case "End":
          event.preventDefault();
          newColIndex = columnKeys.length - 1;
          break;
        case "PageUp":
          event.preventDefault();
          newRowIndex = Math.max(0, activeCell.rowIndex - 10);
          break;
        case "PageDown":
          event.preventDefault();
          newRowIndex = Math.min(
            dataSource.length - 1,
            activeCell.rowIndex + 10
          );
          break;
        default:
          return;
      }

      const newColumnKey = columnKeys[newColIndex];
      const newValue = dataSource[newRowIndex]?.[newColumnKey];

      if (event.shiftKey) {
        // Range selection with Shift + Arrow
        const range: RangeSelection = {
          startRow: Math.min(activeCell.rowIndex, newRowIndex),
          endRow: Math.max(activeCell.rowIndex, newRowIndex),
          startColumn: activeCell.columnKey,
          endColumn: newColumnKey,
        };

        const rangeCells = generateRangeCells(range);
        setSelectedRange(range);
        setSelectedCells(rangeCells);
        onCellSelection?.(rangeCells);
      } else {
        // Single cell navigation
        const newSelection: CellSelection = {
          rowIndex: newRowIndex,
          columnKey: newColumnKey,
          value: newValue,
        };

        setSelectedRange(null);
        setSelectedCells([newSelection]);
        setActiveCell({ rowIndex: newRowIndex, columnKey: newColumnKey });
        onCellSelection?.([newSelection]);
      }
    },
    [
      enableCellSelection,
      activeCell,
      columns,
      dataSource,
      generateRangeCells,
      onCellSelection,
    ]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!enableCellSelection) return;

      if ((event.ctrlKey || event.metaKey) && event.key === "c") {
        event.preventDefault();
        copySelectedCells();
      } else {
        handleKeyboardNavigation(event);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enableCellSelection, copySelectedCells, handleKeyboardNavigation]);

  // Calculate dimensions with browser height limit workaround
  const totalHeight = dataSource.length * rowHeight;
  const selectionColWidth = enableRowSelection ? 48 : 0;
  // Scale checkbox size compactly with row/header height
  const rowCheckboxSize = Math.min(
    Math.max(Math.floor(rowHeight * 0.5), 14),
    20
  );
  // Header checkbox should always match row checkbox size
  const headerCheckboxSize = rowCheckboxSize;
  const totalWidth = columns.reduce((sum, col) => {
    const width = columnWidths[col.key] || col.width || 150;
    return sum + width;
  }, selectionColWidth);

  // Compute per-column unique values lazily when menu opens
  // Use originalDataSource for filtering to show all possible values
  const getColumnValues = useCallback(
    (column: Column): Array<string | number | boolean> => {
      const sourceData = originalDataSource || dataSource;
      const key = column.dataIndex as keyof (typeof sourceData)[number];
      const values: Array<string | number | boolean> = new Array(
        sourceData.length
      );
      for (let i = 0; i < sourceData.length; i++) {
        const v = sourceData[i]?.[key] as unknown;
        if (typeof v === "number" || typeof v === "boolean") values[i] = v;
        else values[i] = v == null ? "" : String(v);
      }
      return values;
    },
    [dataSource, originalDataSource]
  );

  // Browser max height is ~33.5M pixels. Scale down if needed.
  const MAX_HEIGHT = 33000000; // Safe limit
  const heightScale = totalHeight > MAX_HEIGHT ? MAX_HEIGHT / totalHeight : 1;
  const scaledHeight = totalHeight * heightScale;

  // Calculate visible range with overscan (accounting for scale)
  const actualScrollTop = scrollTop / heightScale;
  const startIndex = Math.max(
    0,
    Math.floor(actualScrollTop / rowHeight) - overscan
  );
  const endIndex = Math.min(
    dataSource.length - 1,
    Math.ceil((actualScrollTop + containerHeight) / rowHeight) + overscan
  );

  // Get visible rows (with actual positioning)
  // Include sortState to force recalculation when sorting changes (ensures immediate update)
  const visibleRows = useMemo(() => {
    const rows = [];
    for (let i = startIndex; i <= endIndex; i++) {
      if (dataSource[i]) {
        rows.push({
          index: i,
          data: dataSource[i],
          top: i * rowHeight, // Actual position in unscaled space
        });
      }
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex, endIndex, dataSource, rowHeight, sortState]);

  // Toggle ALL rows (like AG Grid), not just visible rows - OPTIMIZED
  const toggleAllRows = useCallback(() => {
    if (!enableRowSelection) return;

    const allSelected = headerCheckboxState.checked;

    // Deselect all is instant (just clear array)
    if (allSelected) {
      console.log("[Row Selection] Deselecting all rows (instant)");
      if (controlledSelectedRowKeys === undefined) {
        setUncontrolledSelectedRowKeys([]);
      }
      emitRowSelection([]);
      return;
    }

    // Select all: Build keys array (fast) then defer emission (expensive)
    console.log(`[Row Selection] Selecting all ${dataSource.length} rows...`);
    const startTime = performance.now();

    // Show loading indicator immediately
    setIsSelectingAll(true);

    // Build keys array in next tick to allow loading indicator to show
    setTimeout(() => {
      // Build array (fast - just array pushes)
      const allKeys: Array<string | number> = [];
      for (let i = 0; i < dataSource.length; i++) {
        allKeys.push(getRowKey(dataSource[i] as never, i));
      }

      const buildTime = performance.now() - startTime;
      console.log(
        `[Row Selection] ✅ Generated ${
          allKeys.length
        } keys in ${buildTime.toFixed(2)}ms`
      );

      // Update state first (triggers re-render with checkboxes)
      if (controlledSelectedRowKeys === undefined) {
        setUncontrolledSelectedRowKeys(allKeys);
      }

      // Defer emission to prevent blocking (skip expensive row lookup)
      setTimeout(() => {
        emitRowSelection(allKeys, true); // Skip building selectedRows array
        setIsSelectingAll(false);

        const totalTime = performance.now() - startTime;
        console.log(
          `[Row Selection] ✅ Select all complete in ${totalTime.toFixed(
            2
          )}ms (UI responsive)`
        );
      }, 0);
    }, 0);
  }, [
    enableRowSelection,
    headerCheckboxState.checked,
    dataSource,
    getRowKey,
    controlledSelectedRowKeys,
    emitRowSelection,
  ]);

  // Simplified scroll handler - header now scrolls naturally with sticky positioning
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;

    if (scrollRAF.current) {
      return; // Skip if already scheduled
    }

    scrollRAF.current = requestAnimationFrame(() => {
      setScrollTop(target.scrollTop);
      scrollRAF.current = null;
    });
  }, []);

  // Update container dimensions on mount and resize (debounced)
  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { height } = containerRef.current.getBoundingClientRect();
        setContainerHeight(height);
      }
    };

    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        updateDimensions();

        // Close filter dropdown on small screens (mobile/tablet)
        const viewportWidth = window.innerWidth;
        if (viewportWidth < 768 && openFilterKey) {
          // Mobile breakpoint
          setOpenFilterKey(null);
          setOpenFilterValues(null);
          setFilterPosition(null);
        }
      }, 150);
    };

    updateDimensions();
    window.addEventListener("resize", debouncedResize);

    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(resizeTimer);
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
      }
    };
  }, [openFilterKey]);

  // Default theme values
  const defaultTheme = {
    headerBackground: theme.headerBackground || "#fafafa",
    headerColor: theme.headerColor || "#000000",
    rowBackground: theme.rowBackground || "#ffffff",
    rowAlternateBackground: theme.rowAlternateBackground || "#fafafa",
    rowHoverBackground: theme.rowHoverBackground || "#e6f7ff",
    borderColor: theme.borderColor || "#f0f0f0",
    fontSize: theme.fontSize || "14px",
    fontFamily:
      theme.fontFamily ||
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    rowColor: theme.rowColor || "#000000",
  };

  // Memoized render cell content
  const renderCell = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (column: Column, record: any, index: number): React.ReactNode => {
      try {
        const value = record[column.dataIndex];
        // Inline editor
        if (column.editable && column.editor) {
          const emit = (v: unknown) => {
            if (!onRowClick && !onFiltersChange && !onCellSelection) {
              // no-op
            }
            if (typeof onFiltersChange === "function") {
              // keep existing typing satisfied
            }
            if (typeof (onFiltersChange as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onSort as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onRowSelectionChange as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onCellSelection as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onColumnResize as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onColumnReorder as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onSort as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "undefined") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (typeof (onFiltersChange as unknown) === "function") {
              // nothing
            }
            if (onCellEdit) {
              const rowKeyValue = getRowKey(record as never, index);
              onCellEdit({
                rowIndex: index,
                rowKeyValue,
                columnKey: column.key,
                dataIndex: column.dataIndex,
                value: v,
              });
            }
          };

          switch (column.editor.type) {
            case "text":
            case "number":
              return (
                <input
                  className="vg-input"
                  type={column.editor.type}
                  value={value ?? ""}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    emit(
                      column.editor!.type === "number"
                        ? Number(e.target.value)
                        : e.target.value
                    )
                  }
                  onBlur={(e) => e.stopPropagation()}
                />
              );
            case "date":
              return (
                <input
                  className="vg-input"
                  type="date"
                  value={(value ?? "") as string}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => emit(e.target.value)}
                  onBlur={(e) => e.stopPropagation()}
                />
              );
            case "checkbox":
              return (
                <input
                  className="vg-checkbox-input"
                  type="checkbox"
                  checked={Boolean(value)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => emit(e.target.checked)}
                  onBlur={(e) => e.stopPropagation()}
                />
              );
            case "select":
              return (
                <select
                  className="vg-select"
                  value={value ?? ""}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => emit(e.target.value)}
                  onBlur={(e) => e.stopPropagation()}
                >
                  {(column.editor.options || []).map((opt) => (
                    <option key={String(opt.value)} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              );
            default:
              break;
          }
        }

        if (column.render) {
          return column.render(value, record, index);
        }
        return value as React.ReactNode;
      } catch (error) {
        console.error("Error rendering cell:", error);
        return "-";
      }
    },
    [
      onCellEdit,
      getRowKey,
      onCellSelection,
      onColumnReorder,
      onColumnResize,
      onFiltersChange,
      onRowClick,
      onRowSelectionChange,
      onSort,
    ]
  );

  return (
    <div
      className={`virtual-grid ${className}`}
      style={{
        ...style,
        fontFamily: defaultTheme.fontFamily,
        fontSize: defaultTheme.fontSize,
        background: defaultTheme.rowBackground,
        // Propagate theme colors to CSS variables so header cells use them
        // This prevents any transparent/incorrect header background across themes
        // @ts-ignore CSS variables
        "--header-bg": defaultTheme.headerBackground,
        // @ts-ignore CSS variables
        "--header-color": defaultTheme.headerColor,
        // @ts-ignore CSS variables
        "--border-color": defaultTheme.borderColor,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      ref={containerRef}
    >
      {/* Single Scrollable Container with Fixed Header */}
      <div
        className="virtual-grid-scroll-container"
        style={{
          flex: 1,
          overflow: "auto",
          position: "relative",
        }}
        onScroll={handleScroll}
      >
        {/* Fixed Header Overlay */}
        <div
          className="virtual-grid-header-overlay"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            height: `${headerHeight}px`,
            background: defaultTheme.headerBackground,
            color: defaultTheme.headerColor,
            borderBottom: `1px solid ${defaultTheme.borderColor}`,
            overflow: "visible",
          }}
        >
          <div
            className="virtual-grid-header-row"
            style={{
              width: `${totalWidth}px`,
              display: "flex",
              height: "100%",
            }}
          >
            {enableRowSelection && (
              <div
                className="virtual-grid-header-cell"
                style={{
                  width: `${selectionColWidth}px`,
                  minWidth: `${selectionColWidth}px`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRight: `1px solid ${defaultTheme.borderColor}`,
                  position: "relative",
                }}
              >
                <button
                  type="button"
                  aria-label="Select all rows"
                  onClick={toggleAllRows}
                  disabled={isSelectingAll}
                  className={`vg-checkbox ${
                    headerCheckboxState.checked ? "checked" : ""
                  } ${defaultTheme.rowBackground === "#ffffff" ? "" : "dark"}`}
                  style={{
                    width: `${headerCheckboxSize}px`,
                    height: `${headerCheckboxSize}px`,
                    cursor: isSelectingAll ? "wait" : "pointer",
                  }}
                >
                  {headerCheckboxState.checked && (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M20 6L9 17l-5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {!headerCheckboxState.checked &&
                    headerCheckboxState.indeterminate && (
                      <span
                        style={{
                          width: "60%",
                          height: 2,
                          background: "currentColor",
                          borderRadius: 1,
                        }}
                      />
                    )}
                </button>
              </div>
            )}
            {columns.map((column, index) => {
              const isSorted = sortState?.columnKey === column.key;
              const sortDirection = isSorted ? sortState.direction : null;
              const columnWidth =
                columnWidths[column.key] || column.width || 150;
              const isDragging = draggedColumnIndex === index;
              const isDragOver = dragOverColumnIndex === index;

              return (
                <div
                  key={column.key}
                  className={`virtual-grid-header-cell ${
                    column.sortable ? "sortable" : ""
                  } ${isDragging ? "dragging" : ""} ${
                    isDragOver ? "drag-over" : ""
                  }`}
                  style={{
                    width: `${columnWidth}px`,
                    minWidth: `${column.minWidth || columnWidth}px`,
                    textAlign: column.align || "left",
                    padding: "0 12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontWeight: 600,
                    borderRight: `1px solid ${defaultTheme.borderColor}`,
                    overflow: "visible",
                    cursor: column.sortable
                      ? "pointer"
                      : enableColumnReorder
                      ? "grab"
                      : "default",
                    transition: "background-color 0.2s ease, opacity 0.2s ease",
                    position: "relative",
                    opacity: isDragging ? 0.5 : 1,
                    backgroundColor: isDragOver ? "#e6f7ff" : "transparent",
                    borderLeft: isDragOver ? "3px solid #1890ff" : "none",
                  }}
                  title={column.title}
                  onClick={() => {
                    if (draggedColumnIndex === null && column.sortable) {
                      handleColumnSort(column);
                    }
                  }}
                  draggable={enableColumnReorder}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.dropEffect = "move";
                    handleColumnDragStart(index);
                  }}
                  onDragOver={(e) => handleColumnDragOver(index, e)}
                  onDrop={() => handleColumnDrop(index)}
                  onDragEnd={handleColumnDragEnd}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {column.title}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {column.filterable && onFiltersChange && (
                      <button
                        type="button"
                        aria-label={`Filter ${column.title}`}
                        className="vg-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();

                          // Don't open filter on small screens
                          const viewportWidth = window.innerWidth;
                          if (viewportWidth < 768) {
                            return;
                          }

                          const rect = (
                            e.currentTarget as HTMLButtonElement
                          ).getBoundingClientRect();

                          // Calculate responsive position to keep dropdown within viewport
                          const dropdownWidth = 260; // Fixed width from CSS
                          const dropdownHeight = 300; // Approximate height
                          const viewportHeight = window.innerHeight;

                          let left = rect.left;
                          let top = rect.bottom + 4;

                          // Adjust horizontal position if dropdown would go off-screen
                          if (left + dropdownWidth > viewportWidth) {
                            left = viewportWidth - dropdownWidth - 10; // 10px margin
                          }
                          if (left < 10) {
                            left = 10; // 10px margin from left edge
                          }

                          // Adjust vertical position if dropdown would go off-screen
                          if (top + dropdownHeight > viewportHeight) {
                            top = rect.top - dropdownHeight - 4; // Show above button
                          }
                          if (top < 10) {
                            top = 10; // 10px margin from top edge
                          }

                          setFilterPosition({ top, left });
                          setOpenFilterKey((k) => {
                            const nextKey =
                              k === column.key ? null : column.key;
                            if (nextKey) {
                              setOpenFilterValues(getColumnValues(column));
                            } else {
                              setOpenFilterValues(null);
                            }
                            return nextKey;
                          });
                        }}
                      >
                        ☰
                      </button>
                    )}
                    {column.sortable && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginLeft: "8px",
                          flexShrink: 0,
                        }}
                      >
                        {sortDirection === "asc" && (
                          <span style={{ fontSize: "16px", lineHeight: 1 }}>
                            ↑
                          </span>
                        )}
                        {sortDirection === "desc" && (
                          <span style={{ fontSize: "16px", lineHeight: 1 }}>
                            ↓
                          </span>
                        )}
                        {!sortDirection && (
                          <span
                            style={{
                              fontSize: "12px",
                              opacity: 0.4,
                              lineHeight: 1,
                            }}
                          >
                            ⇅
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {openFilterKey === column.key &&
                    column.filterable &&
                    onFiltersChange &&
                    filterPosition &&
                    createPortal(
                      <div
                        className="vg-filter-popover"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: "fixed",
                          top: `${filterPosition.top}px`,
                          left: `${filterPosition.left}px`,
                          zIndex: 2000,
                        }}
                      >
                        <ColumnFilterMenu
                          columnKey={column.key}
                          allValues={
                            openFilterValues || getColumnValues(column)
                          }
                          selected={filters?.[column.key]}
                          onChange={(next) => {
                            const nextAll = { ...(filters || {}) } as Record<
                              string,
                              Set<string | number | boolean>
                            >;
                            if (next.size === 0) delete nextAll[column.key];
                            else nextAll[column.key] = next;
                            onFiltersChange(nextAll);
                          }}
                          onClose={() => {
                            setOpenFilterKey(null);
                            setOpenFilterValues(null);
                          }}
                          dark={defaultTheme.rowBackground !== "#ffffff"}
                        />
                      </div>,
                      document.body
                    )}
                  {enableColumnResize && (
                    <div
                      className="column-resize-handle"
                      style={{
                        position: "absolute",
                        right: "-2px",
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        cursor: "col-resize",
                        backgroundColor:
                          resizingColumn === column.key
                            ? "#1890ff"
                            : "transparent",
                        zIndex: 10,
                      }}
                      onMouseDown={(e) => handleResizeStart(column.key, e)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body Content */}
        <div
          style={{
            height: `${scaledHeight}px`,
            width: `${totalWidth}px`,
            position: "relative",
          }}
        >
          {/* Visible rows */}
          {visibleRows.map((row) => {
            const isEven = row.index % 2 === 0;
            const bgColor = isEven
              ? defaultTheme.rowBackground
              : defaultTheme.rowAlternateBackground;
            const customClassName = rowClassName
              ? rowClassName(row.data, row.index)
              : "";

            const rowDataKey =
              rowKey && row.data[rowKey] ? String(row.data[rowKey]) : row.index;
            const rowKeyValue = getRowKey(row.data as never, row.index);
            const isSelected = selectedRowKeysSet.has(rowKeyValue);

            return (
              <VirtualRow
                key={rowDataKey}
                rowData={row.data}
                rowIndex={row.index}
                rowTop={row.top * heightScale}
                rowHeight={rowHeight}
                totalWidth={totalWidth}
                columns={columns}
                columnWidths={columnWidths}
                borderColor={defaultTheme.borderColor}
                backgroundColor={bgColor}
                hoverBackgroundColor={defaultTheme.rowHoverBackground}
                rowColor={defaultTheme.rowColor}
                onRowClick={onRowClick}
                customClassName={customClassName}
                renderCell={renderCell}
                enableCellSelection={enableCellSelection}
                selectedCells={selectedCells}
                selectedCellsSet={selectedCellsSet}
                activeCell={activeCell}
                onCellClick={handleCellClick}
                onCellMouseDown={handleCellMouseDown}
                onCellMouseMove={handleCellMouseMove}
                onCellMouseUp={handleCellMouseUp}
                enableColumnReorder={enableColumnReorder}
                draggedColumnIndex={draggedColumnIndex}
                dragOverColumnIndex={dragOverColumnIndex}
                enableRowSelection={enableRowSelection}
                isRowSelected={isSelected}
                onToggleRowSelection={() =>
                  toggleRow(row.data as never, row.index)
                }
                selectionColWidth={selectionColWidth}
                checkboxSize={rowCheckboxSize}
              />
            );
          })}
        </div>
      </div>

      {/* Selection Counter Footer (like AG Grid) */}
      {enableRowSelection && selectedRowKeys.length > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "32px",
            background: defaultTheme.headerBackground,
            borderTop: `1px solid ${defaultTheme.borderColor}`,
            display: "flex",
            alignItems: "center",
            padding: "0 16px",
            fontSize: "13px",
            fontWeight: 500,
            color: defaultTheme.headerColor,
            zIndex: 100,
          }}
        >
          <span>
            Selected: {selectedRowKeys.length.toLocaleString()}
            {isSelectingAll && " (selecting...)"}
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(VirtualGrid);
